import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Grid,
  Divider,
  TableSortLabel,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Search,
  Add,
  MoreVert,
  Visibility,
  Edit,
  Business,
  PersonOff,
  Close,
  Person,
  Email,
  Phone,
  Work,
  CalendarToday,
} from "@mui/icons-material";
import api from "../../api/api";
import CustomSnackbar from "../../components/CustomSnackbar";
import translationsMap from "../../en-ja-map";

const t = (key) => translationsMap?.[key] ?? key;

const DoctorManagement = () => {
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionDoctorId, setActionDoctorId] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [formErrors, setFormErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    speciality: "",
    joiningDate: new Date(),
    experience: "",
    gender: "",
    about: "",
  });

  const specialties = [
    "Aesthetic Dermatology",
    "Pediatric Medicine",
    "Cardiology",
    "Cardiologist",
    "Endocrinology",
    "Orthopedics",
    "Neurology",
    "Oncology",
    "Gastroenterology",
  ];

  const genderOptions = ["Male", "Female", "Other", "Prefer not to say"];

  const validateForm = () => {
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = t("First name is required");
    if (!formData.lastName.trim()) errors.lastName = t("Last name is required");
    if (!formData.email.trim()) {
      errors.email = t("Email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("Invalid email format");
    }
    if (!formData.phone.trim()) {
      errors.phone = t("Phone is required");
    } else if (!/^\+?[\d\s-()]+$/.test(formData.phone.replace(/\s/g, ""))) {
      errors.phone = t("Invalid phone format");
    }
    if (!formData.speciality) errors.speciality = t("Specialty is required");
    if (!formData.gender) errors.gender = t("Gender is required");
    if (!formData.experience || formData.experience < 0) {
      errors.experience = t("Experience must be 0 or greater");
    }
    if (!formData.about.trim()) {
      errors.about = t("About section is required");
    } else if (formData.about.length > 1000) {
      errors.about = t("About section must be 1000 characters or less");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const hasFormData = () => {
    return (
      formData.firstName.trim() ||
      formData.lastName.trim() ||
      formData.email.trim() ||
      formData.phone.trim() ||
      formData.speciality ||
      formData.experience ||
      formData.about.trim()
    );
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: "" });
    }
  };

  const handleAddDialogClose = (event, reason) => {
    if (reason === "escapeKeyDown") {
      if (hasFormData()) {
        setShowCancelConfirm(true);
      } else {
        setAddDialogOpen(false);
      }
      return;
    }
    if (reason === "backdropClick" && hasFormData()) {
      return;
    }
    if (reason !== "backdropClick") {
      setAddDialogOpen(false);
    }
  };

  const handleCancelClick = () => {
    if (hasFormData()) {
      setShowCancelConfirm(true);
    } else {
      setAddDialogOpen(false);
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    setAddDialogOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      speciality: "",
      joiningDate: new Date(),
      experience: "",
      gender: "",
      about: "",
    });
    setFormErrors({});
    setSelectedDoctor(null);
    setIsEditing(false);
  };

  const generateAvatar = (name) => {
    const nameParts = name?.split(" ");
    if (nameParts?.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    }
    return name?.substring(0, 2).toUpperCase();
  };

  // Safer date formatting (YYYY/MM/DD) and robust parsing for YYYY-MM-DD strings
  const formatDate = (input) => {
    if (!input) return "";
    let d;
    if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
      const [y, m, day] = input.split("-").map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(input);
    }
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}/${mm}/${dd}`;
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    applySearch();
  }, [doctors, searchTerm]);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/clinic/get/all/doctors/${user.id}`);
      setDoctors(res?.data?.data || []);
    } catch (err) {
      console.error("Error fetching doctors:", err);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    let filtered = [...doctors];
    if (searchTerm) {
      filtered = filtered.filter(
        (doctor) =>
          doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doctor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doctor.speciality.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredDoctors(filtered);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedDoctors = filteredDoctors.sort((a, b) => {
    if (orderBy === "name")
      return order === "asc"
        ? a?.name?.localeCompare(b.name)
        : b?.name?.localeCompare(a.name);
    if (orderBy === "joinDate") {
      const dateA = new Date(a.joined_date);
      const dateB = new Date(b.joined_date);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    if (orderBy === "specialty")
      return order === "asc"
        ? a?.speciality?.localeCompare(b.speciality)
        : b?.speciality?.localeCompare(a.speciality);
    return 0;
  });

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleActionClick = (event, doctorId) => {
    setAnchorEl(event.currentTarget);
    setActionDoctorId(doctorId);
  };
  const handleActionClose = () => {
    setAnchorEl(null);
    setActionDoctorId(null);
  };

  const handleViewProfile = () => {
    const doctor = doctors.find((d) => d.id === actionDoctorId);
    setSelectedDoctor(doctor);
    setViewDialogOpen(true);
    handleActionClose();
  };

  const handleEditProfile = () => {
    const doctor = doctors.find((d) => d.id === actionDoctorId);
    if (!doctor) return;

    const nameParts = doctor.name.split(" ");
    setFormData({
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || "",
      email: doctor.email,
      phone: doctor.phone,
      speciality: doctor.speciality,
      joiningDate: new Date(doctor.joined_date),
      experience: doctor.experience || "",
      gender: doctor.gender || "",
      about: doctor.about || "",
    });

    setSelectedDoctor(doctor);
    setIsEditing(true);
    setAddDialogOpen(true);
    setFormErrors({});
    handleActionClose();
  };

  const handleAddDoctor = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      speciality: "",
      joiningDate: new Date(),
      experience: "",
      gender: "",
      about: "",
    });
    setIsEditing(false);
    setSelectedDoctor(null);
    setFormErrors({});
    setAddDialogOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarType("success");
  };

  const getMyDate = (myDate) => {
    const date = new Date(myDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleFormSubmit = () => {
    if (!validateForm()) {
      setSnackbarMessage(t("Please fix the errors before submitting"));
      setSnackbarOpen(true);
      setSnackbarType("error");
      return;
    }

    if (isEditing && selectedDoctor) {
      const updateDoctor = {
        id: Math.max(...doctors.map((d) => d.id), 0) + 1,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        speciality: formData.speciality,
        is_active: true,
        joined_date: getMyDate(formData.joiningDate),
        experience: formData?.experience,
        gender: formData?.gender,
        about: formData.about,
        created_by: user.id,
      };
      api
        .put(`/v1/clinic/update/doctor/${selectedDoctor.id}`, updateDoctor)
        .then((res) => {
          if (res.status === 200 || res.data.message === "Doctor updated successfully") {
            setSnackbarMessage(t("Doctor updated successfully!"));
            setSnackbarOpen(true);
            setSnackbarType("success");
            fetchDoctors();
          } else {
            setSnackbarMessage(t("Error while updating Doctor"));
            setSnackbarOpen(true);
            setSnackbarType("error");
          }
        })
        .catch((err) => {
          setSnackbarMessage(t("Failed!"));
          setSnackbarOpen(true);
          setSnackbarType("error");
          console.error("Error adding doctor:", err);
        });
    } else {
      const newDoctor = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        speciality: formData.speciality,
        is_active: true,
        joined_date: getMyDate(formData.joiningDate),
        experience: formData?.experience,
        gender: formData?.gender,
        about: formData.about,
        created_by: user.id,
      };
      api
        .post("/v1/clinic/create/doctor", newDoctor)
        .then((res) => {
          if (res.status === 201 || res.data.message === "Doctor added successfully") {
            setSnackbarMessage(t("Doctor added successfully!"));
            setSnackbarOpen(true);
            setSnackbarType("success");
            fetchDoctors();
          } else {
            setSnackbarMessage(t("Error while adding Doctor"));
            setSnackbarOpen(true);
            setSnackbarType("error");
          }
        })
        .catch((err) => {
          setSnackbarMessage(t("Doctor with similar credentails already exists!"));
          setSnackbarOpen(true);
          setSnackbarType("error");
          console.error("Error adding doctor:", err);
        });
    }

    setAddDialogOpen(false);
    setSelectedDoctor(null);
    setIsEditing(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      speciality: "",
      joiningDate: new Date(),
      experience: "",
      gender: "",
      about: "",
    });
    setFormErrors({});
  };

  const handleDeactivate = () => {
    const doctor = doctors.find((d) => d.id === actionDoctorId);
    const obj = { status: !doctor.is_active };
    api
      .put(`v1/clinic/doctor/${actionDoctorId}/status`, obj)
      .then((res) => {
        if (res.status === 200 || res.data.message === "Doctor deactivated successfully") {
          setSnackbarMessage(
            doctor.is_active ? t("Doctor deactivated successfully!") : t("Doctor activated successfully!")
          );
          setSnackbarOpen(true);
          setSnackbarType("success");
          fetchDoctors();
        }
      })
      .catch((err) => {
        setSnackbarMessage(t("Error while updating doctor status"));
        setSnackbarOpen(true);
        setSnackbarType("error");
        console.error("Error deactivating doctor:", err);
        fetchDoctors();
      });
    handleActionClose();
  };

  const paginatedDoctors = sortedDoctors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const stats = [
    { title: t("Total Doctors"), value: doctors.length.toString(), change: "", icon: Person },
    { title: t("Active Doctors"), value: new Set(doctors.map((d) => d.clinic_id)).size.toString(), change: "", icon: Business },
    {
      title: t("New Onboardings"),
      value: doctors
        .filter((d) => {
          const joinDate = new Date(d.joined_date);
          const lastMonth = new Date();
          lastMonth.setMonth(lastMonth.getMonth() - 1);
          return joinDate > lastMonth;
        })
        .length.toString(),
      change: "",
      icon: Add,
    },
  ];

  const rangeFrom = Math.min(page * rowsPerPage + 1, sortedDoctors.length);
  const rangeTo = Math.min((page + 1) * rowsPerPage, sortedDoctors.length);
  const total = sortedDoctors.length;
  const rangeText = t("Showing {{from}}-{{to}} of {{total}}")
    .replace("{{from}}", rangeFrom)
    .replace("{{to}}", rangeTo)
    .replace("{{total}}", total);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ p: 3, maxWidth: "100%", backgroundColor: "background.default", minHeight: "100vh" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 3, mb: 4 }}>
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <Paper key={index} elevation={0} sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "14px", fontWeight: "500", mb: 1 }}>
                        {stat.title}
                      </Typography>
                      <Typography variant="h4" fontWeight="700" color="text.primary" sx={{ fontSize: "28px", mb: 0.5 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "12px", fontWeight: "600", color: "success.main" }}>
                        {stat.change}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 48, height: 48, borderRadius: "12px", backgroundColor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <IconComponent sx={{ color: "white", fontSize: 24 }} />
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>

          <Paper elevation={0}>
            {loading && (
              <Box sx={{ width: "100%", mb: 2 }}>
                <LinearProgress />
              </Box>
            )}

            <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider", background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <TextField
                  placeholder={t("Search doctors...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "text.secondary", fontSize: "20px" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: "350px" }}
                />
                <Button onClick={handleAddDoctor} startIcon={<Add />} variant="contained" color="primary">
                  {t("Add Doctor")}
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === "name"} direction={orderBy === "name" ? order : "asc"} onClick={() => handleSort("name")}>
                        {t("Doctor")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{t("Contact")}</TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === "specialty"} direction={orderBy === "specialty" ? order : "asc"} onClick={() => handleSort("specialty")}>
                        {t("Specialty")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === "joinDate"} direction={orderBy === "joinDate" ? order : "asc"} onClick={() => handleSort("joinDate")}>
                        {t("Join Date")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDoctors.map((doctor, index) => (
                    <TableRow
                      key={doctor.id}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          "& .doctor-avatar": {
                            transform: "scale(1.1)",
                            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}30`,
                          },
                          "& .doctor-name": { color: "primary.main" },
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: "14px", color: "text.primary", py: 2 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            className="doctor-avatar"
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: "12px",
                              fontWeight: "600",
                              backgroundColor: "primary.main",
                              color: "white",
                              transition: "all 0.2s ease",
                              boxShadow: (theme) => `0 2px 8px ${theme.palette.primary.main}20`,
                            }}
                          >
                            {generateAvatar(doctor.name)}
                          </Avatar>
                          <Typography className="doctor-name" variant="body2" fontWeight="500" color="text.primary" sx={{ transition: "color 0.2s ease" }}>
                            {doctor.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: "13px", color: "text.primary", mb: 0.5 }}>
                            {doctor.email}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: "13px", color: "text.secondary", fontFamily: "monospace" }}>
                            {doctor.phone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px", color: "text.primary", py: 2 }}>
                        <Chip
                          label={doctor.speciality}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(25, 118, 210, 0.1)",
                            color: "#1976d2",
                            fontWeight: "600",
                            fontSize: "11px",
                            height: "22px",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={doctor.is_active ? t("Active") : t("Inactive")}
                          size="small"
                          sx={{
                            backgroundColor: doctor.is_active ? "rgba(157, 194, 20, 0.1)" : "rgba(244, 67, 54, 0.1)",
                            color: doctor.is_active ? "success.main" : "error.main",
                            fontWeight: "600",
                            fontSize: "11px",
                            height: "22px",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "14px", color: "text.primary", py: 2 }}>
                        {formatDate(doctor.joined_date)}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <IconButton onClick={(e) => handleActionClick(e, doctor.id)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 3,
                borderTop: "1px solid",
                borderColor: "divider",
                background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "14px", fontWeight: "500" }}>
                {rangeText}
              </Typography>
              <TablePagination
                component="div"
                count={sortedDoctors.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage={t("Rows per page:")}
              />
            </Box>
          </Paper>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleActionClose}
            PaperProps={{
              sx: {
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                border: "1px solid",
                borderColor: "divider",
                mt: 1,
              },
            }}
          >
            <MenuItem
              onClick={handleViewProfile}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => `${theme.palette.primary.main}04`,
                },
              }}
            >
              <Visibility sx={{ mr: 2, fontSize: "18px", color: "text.secondary" }} />
              {t("View Profile")}
            </MenuItem>
            <MenuItem
              onClick={handleEditProfile}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => `${theme.palette.primary.main}04`,
                },
              }}
            >
              <Edit sx={{ mr: 2, fontSize: "18px", color: "text.secondary" }} />
              {t("Edit Profile")}
            </MenuItem>
            <MenuItem
              onClick={handleDeactivate}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => `${theme.palette.primary.main}04`,
                },
              }}
            >
              <PersonOff sx={{ mr: 2, fontSize: "18px", color: "text.secondary" }} />
              {doctors.find((d) => d.id === actionDoctorId)?.is_active ? t("Deactivate") : t("Activate")}
            </MenuItem>
          </Menu>

          <Dialog
            open={viewDialogOpen}
            onClose={() => setViewDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px", boxShadow: "0 24px 48px rgba(0,0,0,0.15)" } }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6" fontWeight="600" color="text.primary">
                  {t("Doctor Profile")}
                </Typography>
                <IconButton onClick={() => setViewDialogOpen(false)} sx={{ color: "text.secondary" }}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3, paddingTop: "10px !important" }}>
              {selectedDoctor && (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        fontSize: "24px",
                        fontWeight: "600",
                        backgroundColor: "primary.main",
                        color: "white",
                        boxShadow: (theme) => `0 4px 16px ${theme.palette.primary.main}30`,
                        flexShrink: 0,
                      }}
                    >
                      {generateAvatar(selectedDoctor.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h5" fontWeight="600" color="text.primary" sx={{ mb: 1 }}>
                        {selectedDoctor.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, lineHeight: 1.6, wordWrap: "break-word", whiteSpace: "pre-wrap" }}
                      >
                        {selectedDoctor.about || t("No description available")}
                      </Typography>
                      <Chip
                        label={selectedDoctor.is_active ? t("Active") : t("Inactive")}
                        size="small"
                        sx={{
                          backgroundColor: selectedDoctor.is_active ? "rgba(157, 194, 20, 0.1)" : "rgba(244, 67, 54, 0.1)",
                          color: selectedDoctor.is_active ? "success.main" : "error.main",
                          fontWeight: "600",
                        }}
                      />
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Email sx={{ color: "primary.main", fontSize: "20px" }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                            {t("Email")}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: "500" }}>
                            {selectedDoctor.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Phone sx={{ color: "primary.main", fontSize: "20px" }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                            {t("Phone")}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: "500" }}>
                            {selectedDoctor.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Work sx={{ color: "primary.main", fontSize: "20px" }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                            {t("Specialty")}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: "500" }}>
                            {selectedDoctor.speciality}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Work sx={{ color: "primary.main", fontSize: "20px" }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                            {t("Experience")}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: "500" }}>
                            {selectedDoctor.exp} {t("years")}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <CalendarToday sx={{ color: "primary.main", fontSize: "20px" }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                            {t("Join Date")}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: "500" }}>
                            {formatDate(selectedDoctor.joined_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Person sx={{ color: "primary.main", fontSize: "20px" }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                            {t("Gender")}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: "500" }}>
                            {selectedDoctor.gender || t("Not specified")}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
          </Dialog>

          <Dialog
            open={addDialogOpen}
            onClose={handleAddDialogClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px", boxShadow: "0 24px 48px rgba(0,0,0,0.15)" } }}
          >
            <DialogTitle sx={{ pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6" fontWeight="600">
                  {isEditing ? t("Edit Doctor") : t("Add new Doctor")}
                </Typography>
                <IconButton onClick={handleCancelClick}>
                  <Close />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isEditing
                  ? t("Update doctor information")
                  : t("Enter the doctor's information and assign them to a clinic.")}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, paddingTop: "16px !important" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 400 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* Labels remain English by requirement */}
                  <TextField
                    label={t("First Name")}
                    value={formData.firstName}
                    onChange={(e) => handleFieldChange("firstName", e.target.value)}
                    error={!!formErrors.firstName}
                    helperText={formErrors.firstName}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label={t("Last Name")}
                    value={formData.lastName}
                    onChange={(e) => handleFieldChange("lastName", e.target.value)}
                    error={!!formErrors.lastName}
                    helperText={formErrors.lastName}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label={t("Email")}
                  value={formData.email}
                  disabled={isEditing}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
                <TextField
                  fullWidth
                  label={t("Phone")}
                  value={formData.phone}
                  onChange={(e) => handleFieldChange("phone", e.target.value)}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                />
                <TextField
                  fullWidth
                  select
                  label={t("Specialty")}
                  value={formData.speciality}
                  onChange={(e) => handleFieldChange("speciality", e.target.value)}
                  error={!!formErrors.speciality}
                  helperText={formErrors.speciality}
                >
                  {specialties.map((specialty) => (
                    <MenuItem key={specialty} value={specialty}>
                      {specialty}
                    </MenuItem>
                  ))}
                </TextField>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <DatePicker
                    label={t("Joining Date")}
                    value={formData.joiningDate}
                    onChange={(newValue) => handleFieldChange("joiningDate", newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                    maxDate={new Date()}
                  />
                  <Button variant="outlined" onClick={() => handleFieldChange("joiningDate", new Date())} sx={{ minWidth: "80px", height: "56px" }}>
                    {t("Today")}
                  </Button>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    select
                    label={t("Gender")}
                    value={formData.gender}
                    onChange={(e) => handleFieldChange("gender", e.target.value)}
                    error={!!formErrors.gender}
                    helperText={formErrors.gender}
                    sx={{ flex: 1 }}
                  >
                    {genderOptions.map((gender) => (
                      <MenuItem key={gender} value={gender}>
                        {t(gender)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label={t("Experience (years)")}
                    value={formData.experience}
                    onChange={(e) => handleFieldChange("experience", e.target.value)}
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    error={!!formErrors.experience}
                    helperText={formErrors.experience}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label={t("About Doctor")}
                  multiline
                  rows={4}
                  value={formData.about}
                  onChange={(e) => handleFieldChange("about", e.target.value)}
                  error={!!formErrors.about}
                  helperText={formErrors.about || `${formData.about.length}/1000 ${t("characters")}`}
                  inputProps={{ maxLength: 1000 }}
                  placeholder={t("Tell us about this doctor's background, achievements, or specializations...")}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={handleCancelClick}
                sx={{
                  "&:hover": {
                    backgroundColor: "error.light",
                    color: "white",
                    transform: "scale(1.02)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {t("Cancel")}
              </Button>
              <Button onClick={handleFormSubmit} variant="contained" color="primary">
                {isEditing ? t("Update Doctor") : t("Add Doctor")}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            PaperProps={{ sx: { borderRadius: "16px", boxShadow: "0 24px 48px rgba(0,0,0,0.15)" } }}
          >
            <DialogTitle sx={{ pb: 2 }}>
              <Typography variant="h6" fontWeight="600">
                {t("Unsaved Changes")}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                {t("You have unsaved changes. Are you sure you want to cancel? All changes will be lost.")}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setShowCancelConfirm(false)} color="primary">
                {t("Keep Editing")}
              </Button>
              <Button
                onClick={confirmCancel}
                variant="contained"
                color="error"
                sx={{
                  "&:hover": {
                    backgroundColor: "error.dark",
                    transform: "scale(1.02)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {t("Discard Changes")}
              </Button>
            </DialogActions>
          </Dialog>

          {!loading && sortedDoctors.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                px: 4,
                mt: 4,
                backgroundColor: "background.paper",
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                transition: "all 0.3s ease",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: (theme) => `${theme.palette.primary.main}10`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.05)" },
                }}
              >
                <Search sx={{ fontSize: 40, color: "primary.main", opacity: 0.8 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 1,
                  textAlign: "center",
                  fontSize: "1.5rem",
                  letterSpacing: "-0.02em",
                }}
              >
                {t("No Doctors Found")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  mb: 4,
                  maxWidth: "400px",
                  lineHeight: 1.6,
                }}
              >
                {searchTerm
                  ? t("No doctors match your search criteria.")
                  : t("No doctors have been added to the system yet.")}
              </Typography>
              <Button onClick={handleAddDoctor} variant="outlined" color="primary" sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: "0.95rem" }}>
                {t("Add Doctor")}
              </Button>
            </Box>
          )}
        </Box>
      </LocalizationProvider>
      <CustomSnackbar onClose={handleCloseSnackbar} isOpen={snackbarOpen} message={snackbarMessage} type={snackbarType} />
    </>
  );
};

export default DoctorManagement;
